import {useDeepMemo} from '@wandb/weave/hookUtils';
import {useCallback, useEffect, useMemo, useState} from 'react';

import {TraceServerClient} from '../wfReactInterface/traceServerClient';
import {useGetTraceServerClientContext} from '../wfReactInterface/traceServerClientContext';

type HookResult<T> =
  | {
      loading: true;
      data: T | null;
      error: Error | null;
      refetch: () => void;
    }
  | {
      loading: false;
      error: Error;
      data: null;
      refetch: () => void;
    }
  | {
      loading: false;
      data: T;
      error: null;
      refetch: () => void;
    };

type HookifyOptions<TArgs extends any[]> = {
  /**
   * Enable caching of results. When enabled, results are cached globally
   * per unique set of arguments. Subsequent calls with the same arguments
   * will return cached data immediately without showing a loading state.
   * Calling refetch() will bypass the cache and update it with fresh data.
   */
  cache?: boolean;
  /**
   * Optional function to generate a cache key from the arguments.
   * By default, uses JSON.stringify on all arguments.
   * Use this when arguments contain non-serializable objects or when you want
   * to cache based on a subset of the arguments.
   *
   * Example: (...args) => args.slice(1).join('|') // Skip first arg (e.g., client object)
   */
  cacheKey?: (...args: TArgs) => string;
};

export const hookify = <TArgs extends any[], TReturn>(
  promiseFn: (...inputs: TArgs) => Promise<TReturn>,
  options?: HookifyOptions<TArgs>
): ((...inputs: TArgs) => HookResult<TReturn>) => {
  // Create a cache that's shared across all instances of this hook
  const cache = options?.cache ? new Map<string, TReturn>() : null;
  const cacheKeyFn = options?.cacheKey;

  const useFn = (...inputs: TArgs) => {
    const deepInputs = useDeepMemo(inputs);

    // Generate cache key from inputs
    const cacheKey = useMemo(() => {
      if (!cache) return null;
      return cacheKeyFn
        ? cacheKeyFn(...deepInputs)
        : JSON.stringify(deepInputs);
    }, [deepInputs]);

    // Check cache for existing data
    const cachedData = useMemo(
      () => (cacheKey && cache ? cache.get(cacheKey) : undefined),
      [cacheKey]
    );

    const [data, setData] = useState<TReturn | null>(cachedData ?? null);
    const [error, setError] = useState<Error | null>(null);
    const [loading, setLoading] = useState(cachedData === undefined);

    const refetch = useCallback(() => {
      const resultsFor = deepInputs;
      let mounted = true;
      setLoading(true);
      setError(null);
      promiseFn(...deepInputs)
        .then(res => {
          if (!mounted || resultsFor !== deepInputs) {
            return;
          }
          setData(res);
          setError(null);
          // Update cache on successful fetch
          if (cacheKey && cache) {
            cache.set(cacheKey, res);
          }
        })
        .catch(error => {
          if (!mounted || resultsFor !== deepInputs) {
            return;
          }
          setData(null);
          setError(error);
        })
        .finally(() => {
          if (!mounted || resultsFor !== deepInputs) {
            return;
          }
          setLoading(false);
        });
      return () => {
        mounted = false;
      };
    }, [deepInputs, cacheKey]);

    useEffect(() => {
      if (cachedData !== undefined) {
        // If we have cached data, use it immediately
        setData(cachedData);
        setError(null);
        setLoading(false);
        return undefined;
      } else {
        // Otherwise, fetch fresh data
        const cleanup = refetch();
        return cleanup;
      }
    }, [refetch, cachedData]);

    const res = useMemo(() => {
      if (loading) {
        return {
          loading,
          data,
          error,
          refetch,
        };
      } else if (error) {
        return {
          loading,
          error,
          data: null,
          refetch,
        };
      } else {
        return {
          loading,
          error: null,
          // A little dangerous in the event that `data` is null but `TReturn` is not
          // but it's fine for our use case
          data: data as TReturn,
          refetch,
        };
      }
    }, [loading, data, error, refetch]);
    return res;
  };
  return useFn;
};

export const clientBound = <TArgs extends any[], TReturn>(
  useHook: (client: TraceServerClient, ...inputs: TArgs) => HookResult<TReturn>
) => {
  const useFn = (...inputs: TArgs) => {
    const client = useGetTraceServerClientContext()();
    return useHook(client, ...inputs);
  };
  return useFn;
};
